import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Sparkles, RotateCcw } from 'lucide-react';
import { getReply, getReplyById } from '../utils/chatbot-engine';
import { starterQuestions, intentLabels } from '../data/chatbot-knowledge';

const LOGO_URL = process.env.REACT_APP_LOGO_URL;

// Renders markdown-lite : **bold** and line breaks
function renderText(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="text-yellow-400 font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return (
      <React.Fragment key={i}>
        {part.split('\n').map((line, j, arr) => (
          <React.Fragment key={j}>
            {line}
            {j < arr.length - 1 && <br />}
          </React.Fragment>
        ))}
      </React.Fragment>
    );
  });
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1.5 px-1 py-2">
      <span className="h-2 w-2 rounded-full bg-yellow-400/80 animate-[bounce_1.2s_-0.32s_ease-in-out_infinite]" />
      <span className="h-2 w-2 rounded-full bg-yellow-400/80 animate-[bounce_1.2s_-0.16s_ease-in-out_infinite]" />
      <span className="h-2 w-2 rounded-full bg-yellow-400/80 animate-[bounce_1.2s_0s_ease-in-out_infinite]" />
    </div>
  );
}

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(true);
  const [messages, setMessages] = useState([
    {
      role: 'bot',
      text: "Salut ! 🐝 Je suis **BuzzBot**, l'assistant officiel de BuzzBump. Pose-moi une question sur l'application, ou choisis un sujet ci-dessous.",
      related: starterQuestions.map((s) => s.id),
    },
  ]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Auto scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, typing]);

  // Focus input when opening
  useEffect(() => {
    if (open) {
      setHasUnread(false);
      setTimeout(() => inputRef.current?.focus(), 350);
    }
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const sendUserMessage = (text) => {
    if (!text.trim()) return;
    const userMsg = { role: 'user', text: text.trim() };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setTyping(true);

    // Simulate thinking time (natural feel)
    const reply = getReply(text);
    const thinkTime = 600 + Math.min(reply.text.length * 8, 1400);

    setTimeout(() => {
      setTyping(false);
      setMessages((m) => [...m, { role: 'bot', text: reply.text, related: reply.related }]);
    }, thinkTime);
  };

  const handleSuggestion = (intentId) => {
    const label = intentLabels[intentId] || intentId;
    setMessages((m) => [...m, { role: 'user', text: label }]);
    setTyping(true);

    const reply = getReplyById(intentId);
    const thinkTime = 600 + Math.min((reply?.text.length || 0) * 8, 1400);

    setTimeout(() => {
      setTyping(false);
      if (reply) {
        setMessages((m) => [...m, { role: 'bot', text: reply.text, related: reply.related }]);
      }
    }, thinkTime);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendUserMessage(input);
  };

  const resetConversation = () => {
    setMessages([
      {
        role: 'bot',
        text: "Conversation réinitialisée ! 🐝 Pose-moi une nouvelle question.",
        related: starterQuestions.map((s) => s.id),
      },
    ]);
  };

  return (
    <>
      {/* Floating button */}
      <button
        aria-label={open ? 'Fermer le chat' : 'Ouvrir le chat'}
        onClick={() => setOpen((o) => !o)}
        className={`fixed bottom-5 right-5 md:bottom-7 md:right-7 z-[60] h-14 w-14 md:h-16 md:w-16 rounded-full grid place-items-center shadow-xl shadow-yellow-400/20 transition-all duration-300 ${
          open
            ? 'bg-neutral-800 text-yellow-400 rotate-90 scale-90'
            : 'bg-yellow-400 text-neutral-900 hover:scale-110 hover:shadow-yellow-400/40'
        }`}
      >
        {open ? (
          <X size={26} strokeWidth={2.5} />
        ) : (
          <>
            <MessageCircle size={26} strokeWidth={2.5} />
            {hasUnread && (
              <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-red-500 border-2 border-neutral-950 animate-pulse" />
            )}
            <span className="absolute inset-0 rounded-full bg-yellow-400 animate-ping opacity-20 pointer-events-none" />
          </>
        )}
      </button>

      {/* Chat panel */}
      <div
        className={`fixed z-[59] bg-neutral-950 border border-yellow-400/20 shadow-2xl shadow-black/60
        transition-all duration-300 origin-bottom-right
        flex flex-col overflow-hidden
        ${open ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'}
        bottom-0 right-0 left-0 top-0 md:bottom-24 md:right-7 md:left-auto md:top-auto
        md:rounded-2xl md:w-[400px] md:h-[600px] md:max-h-[80vh]`}
        role="dialog"
        aria-label="Chat BuzzBump"
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-neutral-900 to-neutral-950 border-b border-yellow-400/15">
          <div className="relative">
            {LOGO_URL ? (
              <img src={LOGO_URL} alt="BuzzBot avatar" className="h-10 w-10 object-contain rounded-md bg-neutral-900 p-1" />
            ) : (
              <div className="h-10 w-10 grid place-items-center rounded-md bg-yellow-400 text-neutral-900 font-bold">B</div>
            )}
            <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-green-500 border-2 border-neutral-950" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-semibold flex items-center gap-1.5">
              BuzzBot
              <Sparkles size={14} className="text-yellow-400" />
            </div>
            <div className="text-xs text-neutral-400">Assistant en ligne</div>
          </div>
          <button
            onClick={resetConversation}
            aria-label="Réinitialiser"
            className="h-9 w-9 grid place-items-center rounded-md text-neutral-400 hover:text-yellow-400 hover:bg-white/5 transition-colors"
            title="Réinitialiser la conversation"
          >
            <RotateCcw size={16} />
          </button>
          <button
            onClick={() => setOpen(false)}
            aria-label="Fermer"
            className="h-9 w-9 grid place-items-center rounded-md text-neutral-400 hover:text-yellow-400 hover:bg-white/5 transition-colors md:hidden"
          >
            <X size={18} />
          </button>
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-5 space-y-4 bg-neutral-950"
          style={{ scrollbarWidth: 'thin' }}
        >
          {messages.map((msg, i) => (
            <MessageBubble
              key={i}
              role={msg.role}
              text={msg.text}
              related={msg.related}
              isLast={i === messages.length - 1}
              onSuggestion={handleSuggestion}
            />
          ))}
          {typing && (
            <div className="flex items-end gap-2">
              <div className="h-8 w-8 rounded-full bg-yellow-400/10 grid place-items-center text-yellow-400 text-xs font-bold flex-shrink-0">
                🐝
              </div>
              <div className="bg-neutral-900 border border-white/10 rounded-2xl rounded-bl-sm px-3 py-1">
                <TypingDots />
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <form
          onSubmit={handleSubmit}
          className="flex items-center gap-2 px-3 py-3 border-t border-white/10 bg-neutral-900/60"
        >
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Pose ta question..."
            className="flex-1 px-4 py-2.5 rounded-full bg-neutral-950 border border-white/10 text-white text-sm placeholder-neutral-500 focus:outline-none focus:border-yellow-400/60 focus:ring-2 focus:ring-yellow-400/20 transition-colors"
            maxLength={300}
          />
          <button
            type="submit"
            disabled={!input.trim() || typing}
            aria-label="Envoyer"
            className="h-10 w-10 grid place-items-center rounded-full bg-yellow-400 text-neutral-900 hover:bg-yellow-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
          >
            <Send size={16} />
          </button>
        </form>
        <div className="px-4 pb-2 text-[10px] text-neutral-600 text-center bg-neutral-900/60">
          BuzzBot répond avec une base de connaissances locale.
        </div>
      </div>
    </>
  );
}

function MessageBubble({ role, text, related, isLast, onSuggestion }) {
  const isUser = role === 'user';
  return (
    <div className={`flex items-end gap-2 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div
        className={`h-8 w-8 rounded-full grid place-items-center text-xs font-bold flex-shrink-0 ${
          isUser ? 'bg-yellow-400 text-neutral-900' : 'bg-yellow-400/10 text-yellow-400'
        }`}
      >
        {isUser ? 'Toi' : '🐝'}
      </div>
      <div className={`max-w-[78%] ${isUser ? 'items-end' : 'items-start'} flex flex-col gap-2`}>
        <div
          className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed animate-[fadeIn_0.4s_ease-out] ${
            isUser
              ? 'bg-yellow-400 text-neutral-900 rounded-br-sm font-medium'
              : 'bg-neutral-900 border border-white/10 text-neutral-200 rounded-bl-sm'
          }`}
        >
          {renderText(text)}
        </div>
        {!isUser && isLast && related && related.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {related.map((rid) => (
              <button
                key={rid}
                onClick={() => onSuggestion(rid)}
                className="px-3 py-1.5 rounded-full bg-yellow-400/10 border border-yellow-400/30 text-yellow-400 text-xs hover:bg-yellow-400 hover:text-neutral-900 transition-colors"
              >
                {intentLabels[rid] || rid}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
